"use client";

import { Button } from "@/components/rac/button";
import { Tooltip, TooltipTrigger } from "@/components/rac/tooltip";
import { MapItemSUP } from "@/types/search";
import { trackEvent } from "@/utils/analytics/track-event";
import { GitCompare, Loader2 } from "lucide-react";
import { ButtonProps } from "react-aria-components";

export interface CompareButtonProps extends Omit<ButtonProps, "onPress"> {
	property: MapItemSUP;
	isInComparison?: boolean;
	onToggleComparison?: (property: MapItemSUP) => void;
	isLoading?: boolean;
	variant?: "icon" | "full";
}

export function CompareButton({
	property,
	isInComparison = false,
	onToggleComparison,
	isLoading = false,
	variant = "icon",
	className,
	...props
}: CompareButtonProps) {
	const handleClick = () => {
		if (onToggleComparison) {
			onToggleComparison(property);

			// Track analytics
			trackEvent({
				google: {
					event: isInComparison ? "remove_from_comparison" : "add_to_comparison",
					options: {
						property_id: property._id,
						property_type: property.type,
						source: "compare_button",
					},
				},
			});
		}
	};

	// Event handler to stop propagation when clicking the button
	const handleNativeClick = (e: React.MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const ariaLabel = isInComparison ? "Remove from comparison" : "Add to comparison";

	if (variant === "icon") {
		return (
			<TooltipTrigger>
				<Button
					variant={isInComparison ? "bareshell" : "outline"}
					size="icon"
					className={`w-10 h-10 rounded-xl border-gray-300 hover:bg-gray-50 transition-colors data-[hovered]:translate-y-0 ${className || ""}`}
					isPending={isLoading}
					isDisabled={isLoading}
					onPress={handleClick}
					onClick={handleNativeClick}
					aria-label={ariaLabel}
					{...props}
				>
					{isLoading ? (
						<Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" />
					) : (
						<GitCompare
							className={`w-5 h-5 ${isInComparison ? "fill-primary text-primary-700" : ""}`}
							aria-hidden="true"
						/>
					)}
				</Button>
				<Tooltip placement="left">{ariaLabel}</Tooltip>
			</TooltipTrigger>
		);
	}

	return (
		<Button
			variant={isInComparison ? "accent" : "outline"}
			size="sm"
			className={`flex items-center gap-2 ${className || ""}`}
			isPending={isLoading}
			isDisabled={isLoading}
			onPress={handleClick}
			onClick={handleNativeClick}
			{...props}
		>
			{isLoading ? (
				<>
					<Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
					<span>Processing...</span>
				</>
			) : (
				<>
					<GitCompare className="w-4 h-4" aria-hidden="true" />
					<span>{isInComparison ? "In Comparison" : "Compare"}</span>
				</>
			)}
		</Button>
	);
}
